/**
 * <s-variant-media> — Shows the selected variant's image in the product gallery.
 *
 * Lives inside the "product-form" partial, so it reconnects every time s-variant-picker swaps
 * that partial after a variant change. On each connect it asks <s-product-gallery> to scroll to
 * the slide whose [data-media-id] matches its own data-media-id, which also moves the active
 * thumbnail on PC. After a change on PC, the page scrolls up to the gallery if it is out of view.
 *
 * Element dataset:
 *   data-media-id — The selected variant's featured media id (empty when it has no image).
 *
 * External coupling:
 *   s-product-gallery (global, one per product page) — Provides showMedia(mediaId).
 */
class SVariantMedia extends HTMLElement {
  static #initialized = false;

  async connectedCallback() {
    const isChange = SVariantMedia.#initialized;
    SVariantMedia.#initialized = true;

    const mediaId = this.dataset.mediaId;
    if (!mediaId) return;

    await customElements.whenDefined('s-product-gallery');
    const gallery = document.querySelector('s-product-gallery');
    if (!gallery || typeof gallery.showMedia !== 'function') return;
    gallery.showMedia(mediaId);

    // Desktop only: bring the gallery into view after a change. On mobile the swipe gallery sits
    // just above the options, so the page stays put.
    if (!isChange || !matchMedia('(min-width: 768px)').matches) return;
    if (gallery.getBoundingClientRect().top < 0) {
      const behavior = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      gallery.scrollIntoView({ block: 'start', behavior });
    }
  }
}

if (!customElements.get('s-variant-media')) customElements.define('s-variant-media', SVariantMedia);
